import Divider from 'components/Divider';
import { DarkSparkles } from 'components/Sparkles/DarkSparkles';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { ChevronForward, Leaf, LockClosed, Speedometer } from 'react-ionicons';
import { motion } from 'framer-motion';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { SearchProps } from './Search';

interface TailedOptionsProps extends Pick<SearchProps, 'addressValue'> {
  onSearchAgain: () => void;
}

const TailedOptions: FC<TailedOptionsProps> = ({
  onSearchAgain,
  addressValue,
}) => {
  const { oktaAuth } = useOktaAuth();

  const onSignUp = () => {
    const originalUri = toRelativeUrl(
      `/access-express/v3/access/product/All`,
      window.location.origin,
    );
    oktaAuth.setOriginalUri(originalUri);
    oktaAuth.signInWithRedirect({
      state: JSON.stringify(addressValue),
      extraParams: {
        view: 'signup',
      },
    });
  };
  return (
    <div className="relative z-10 mx-auto flex max-w-[911px] flex-col items-center pt-[100px]">
      <div className="space-y-8 bg-background-base-surface-2 p-6 shadow-elevation1">
        <div className="flex flex-col gap-4 bg-background-base-surface-1 p-4">
          <p className="text-xl font-bold text-content-base-default">
            Your tailored options
          </p>
          <p className="font-semibold text-content-base-subdued">
            We’ve prepared three recommendations based on your needs. Log in to
            view exact pricing and place your order.
          </p>
          <Divider />
          <div className="mt-2 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <TailedOption
              title="Starter"
              onSignUp={onSignUp}
              icon={<Leaf width="16px" height="16px" color="currentColor" />}
              prices={[
                '40',
                '55',
                '60',
                '65',
                '70',
                '75',
                '80',
                '85',
                '90',
                '100',
              ]}
              bandwidthSpeed="100-250"
              bandwidth="Mbps"
            />
            <TailedOption
              title="Performance"
              onSignUp={onSignUp}
              icon={
                <Speedometer width="16px" height="16px" color="currentColor" />
              }
              prices={[
                '90',
                '95',
                '100',
                '105',
                '110',
                '115',
                '120',
                '125',
                '130',
                '135',
                '140',
                '145',
                '150',
                '155',
                '160',
                '165',
                '170',
                '175',
                '180',
                '185',
                '190',
                '195',
                '200',
              ]}
              bandwidthSpeed="1-1.25"
              bandwidth="Gbps"
            />
            <TailedOption
              title="Best Value"
              onSignUp={onSignUp}
              icon={<DarkSparkles fill="currentColor" />}
              prices={[
                '60',
                '65',
                '70',
                '75',
                '80',
                '85',
                '90',
                '95',
                '100',
                '105',
                '110',
                '115',
                '120',
              ]}
              bandwidthSpeed="250-500"
              bandwidth="Mbps"
            />
          </div>
        </div>
        <div className="flex flex-col justify-between gap-8 md:flex-row">
          <p className="text-content-base-subdued ">
            Estimates shown here. Actual pricing available after login. <br />
            Availability and rates may vary.
          </p>
          <GraniteButton
            variant="secondary"
            className="!h-fit whitespace-nowrap"
            size="large"
            onClick={onSearchAgain}
          >
            Search again
          </GraniteButton>
        </div>
      </div>
    </div>
  );
};

export default TailedOptions;

interface TailedOptionProps {
  title: string;
  icon: ReactNode;
  prices: string[];
  bandwidth: string;
  bandwidthSpeed: string;
  onSignUp: () => void;
}

const TailedOption: FC<TailedOptionProps> = ({
  title,
  icon,
  prices,
  bandwidthSpeed,
  bandwidth,
  onSignUp,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const { dollarColumn, digitColumns } = useMemo(() => {
    const maxLength = Math.max(...prices.map((p) => p.length));
    const dollarColumn = Array(prices.length).fill('$');

    const digitColumns: string[][] = [];
    for (let i = 0; i < maxLength; i++) {
      digitColumns.push(prices.map((p) => p[i] ?? '')); // <-- no left padding
    }

    return { dollarColumn, digitColumns };
  }, [prices]);

  useEffect(() => {
    let active = true;

    const loop = async () => {
      while (active) {
        setCurrentIndex((prev) => {
          const half = Math.floor(prices.length / 2);
          let next = prev;

          while (Math.abs(next - prev) < half) {
            next = Math.floor(Math.random() * prices.length);
          }
          return next;
        });
        await new Promise((res) => setTimeout(res, 2000));
      }
    };

    const startTimer = setTimeout(loop, 1000);
    return () => {
      active = false;
      clearTimeout(startTimer);
    };
  }, [prices.length]);

  return (
    <div className="group flex w-full flex-col items-center gap-4 rounded-lg bg-background-base-surface-3 p-4">
      <p className="flex items-center gap-2 font-semibold text-content-base-default">
        <span className="text-content-base-subdued">{icon}</span>
        {title}
      </p>
      <div className="flex w-full flex-1 flex-col items-center justify-between gap-4 rounded bg-background-base-surface-2 p-3 text-center">
        <div>
          <p className="text-xs font-bold text-content-base-subdued ">
            Average MRC
          </p>
          <div className="relative h-[42px] w-fit overflow-hidden">
            <div className="text-[28px] font-bold text-content-accent-default group-hover:text-content-base-default">
              {dollarColumn.map((_, index) => (
                <div key={index} className="ml-2.5 flex">
                  <motion.div
                    animate={{ y: -currentIndex * 42 }}
                    transition={{
                      type: 'spring',
                      mass: 1,
                      stiffness: 25,
                      damping: 7.5,
                    }}
                  >
                    $
                  </motion.div>
                  {digitColumns.map((chars, i) => (
                    <motion.div
                      key={`price_${i}`}
                      animate={{ y: -currentIndex * 42 }}
                      transition={{
                        type: 'spring',
                        mass: 1,
                        stiffness: 25,
                        damping: 7.5,
                        delay: i * 0.1,
                      }}
                    >
                      {chars[index]}
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-content-base-subdued ">
            Average bandwidth
          </p>
          <p className=" text-xl font-bold text-content-base-default">
            {bandwidthSpeed} <span className="text-base">{bandwidth}</span>
          </p>
        </div>
      </div>
      <GraniteButton className="w-full !px-2" onClick={onSignUp}>
        <LockClosed width="14px" height="14px" color="currentColor" />
        Sign up to view pricing
        <ChevronForward width="14px" height="14px" color="currentColor" />
      </GraniteButton>
    </div>
  );
};
